import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  UsersIcon, 
  UserGroupIcon, 
  ShieldCheckIcon,
  CalendarIcon,
  EnvelopeIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { UserService, LeagueService, RoleService, TeamService } from '../../services/firebaseService';
import { USER_ROLES } from '../../models/dataModels';
import { getUserClaimedTeams } from '../../utils/teamUtils';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [roles, setRoles] = useState([]);
  const [teamsMap, setTeamsMap] = useState({}); // Map of 'leagueId:seasonId:teamId' -> team object
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadUsersData();
  }, []);

  const loadUsersData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load all users, leagues, and roles in parallel
      const [usersData, leaguesData, rolesData] = await Promise.all([
        UserService.getAllUsers(),
        LeagueService.getAllLeagues(),
        RoleService.getAllRoles()
      ]);

      setUsers(usersData);
      setLeagues(leaguesData);
      setRoles(rolesData);

      // Load all teams for all league-seasons that users have claimed
      await loadTeamsData(usersData);
    } catch (error) {
      console.error('Error loading users data:', error);
      setError('Failed to load users data');
    } finally {
      setLoading(false);
    }
  };

  const loadTeamsData = async (usersData) => {
    try {
      // Collect all unique league-season pairs from all users
      const leagueSeasonPairs = new Set(); // Set of 'leagueId:seasonId' for batch loading

      usersData.forEach(user => {
        if (user.teams) {
          const claimedTeams = getUserClaimedTeams(user.teams);
          claimedTeams.forEach(claimedTeam => {
            leagueSeasonPairs.add(`${claimedTeam.leagueId}:${claimedTeam.seasonId}`);
          });
        }
      });

      // Load all teams for all unique league-season pairs in parallel
      const teamPromises = Array.from(leagueSeasonPairs).map(async (pair) => {
        const [leagueId, seasonId] = pair.split(':');
        try {
          const teams = await TeamService.getTeamsForLeagueSeason(leagueId, seasonId);
          return { leagueId, seasonId, teams };
        } catch (error) {
          console.warn(`Error loading teams for ${leagueId}:${seasonId}:`, error);
          return { leagueId, seasonId, teams: [] };
        }
      });

      const teamResults = await Promise.all(teamPromises);

      // Build map of 'leagueId:seasonId:teamId' -> team object
      const teamsMapData = {};
      teamResults.forEach(({ leagueId, seasonId, teams }) => {
        teams.forEach(team => {
          const key = `${leagueId}:${seasonId}:${team.id}`;
          teamsMapData[key] = team;
        });
      });

      setTeamsMap(teamsMapData);
    } catch (error) {
      console.error('Error loading teams data:', error);
      // Don't fail the whole page if teams can't be loaded
    }
  };

  const getUserLeagueMemberships = (user) => {
    const memberships = [];
    
    // Check teams (player memberships)
    if (user.teams) {
      const claimedTeams = getUserClaimedTeams(user.teams);
      
      // Group by league-season for better display
      const leagueSeasonGroups = {};
      claimedTeams.forEach(claimedTeam => {
        const key = `${claimedTeam.leagueId}:${claimedTeam.seasonId}`;
        if (!leagueSeasonGroups[key]) {
          leagueSeasonGroups[key] = {
            leagueId: claimedTeam.leagueId,
            seasonId: claimedTeam.seasonId,
            teams: []
          };
        }
        leagueSeasonGroups[key].teams.push(claimedTeam);
      });

      // Create memberships for each league-season combination
      Object.values(leagueSeasonGroups).forEach(group => {
        const league = leagues.find(l => l.id === group.leagueId);
        if (league) {
          // Get team names from teamsMap
          const teamNames = group.teams.map(claimedTeam => {
            const teamKey = `${claimedTeam.leagueId}:${claimedTeam.seasonId}:${claimedTeam.teamId}`;
            const team = teamsMap[teamKey];
            return team ? team.name : `Team ${claimedTeam.teamId}`;
          });

          memberships.push({
            leagueId: group.leagueId,
            leagueName: league.name,
            seasonId: group.seasonId,
            role: 'Player',
            teamIds: group.teams.map(t => t.teamId),
            teamNames: teamNames
          });
        }
      });
    }

    // Check managed leagues (league manager memberships)
    if (user.managedLeagues) {
      user.managedLeagues.forEach(leagueId => {
        const league = leagues.find(l => l.id === leagueId);
        if (league) {
          memberships.push({
            leagueId,
            leagueName: league.name,
            role: 'League Manager'
          });
        }
      });
    }

    return memberships;
  };

  const getRoleBadgeColor = (roleId) => {
    switch (roleId) {
      case USER_ROLES.ADMIN:
        return 'bg-red-100 text-red-800';
      case USER_ROLES.LEAGUE_MANAGER:
        return 'bg-blue-100 text-blue-800';
      case USER_ROLES.PLAYER:
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : roleId;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Never';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-survivor-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadUsersData}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <UsersIcon className="h-8 w-8 text-gray-400 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Users</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Manage all users, their roles, and league memberships
                </p>
              </div>
            </div>
            <Link
              to="/admin/users/create"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create User
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="px-4 py-6 sm:px-0 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <UsersIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Users
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {users.length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ShieldCheckIcon className="h-6 w-6 text-red-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Admins
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {users.filter(u => u.roles?.includes(USER_ROLES.ADMIN)).length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <UserGroupIcon className="h-6 w-6 text-blue-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        League Managers
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {users.filter(u => u.roles?.includes(USER_ROLES.LEAGUE_MANAGER)).length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <UserGroupIcon className="h-6 w-6 text-green-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Players
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {users.filter(u => u.roles?.includes(USER_ROLES.PLAYER)).length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                All Users
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Complete list of users with their roles and league memberships
              </p>
            </div>
            
            {users.length === 0 ? (
              <div className="text-center py-12">
                <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No users</h3>
                <p className="mt-1 text-sm text-gray-500">No users have been created yet.</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {users.map((user) => {
                  const memberships = getUserLeagueMemberships(user);
                  return (
                    <li key={user.id} className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <span className="text-sm font-medium text-gray-700">
                                  {user.displayName?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {user.displayName || 'No name'}
                                </p>
                                <div className="flex space-x-1">
                                  {user.roles?.map((roleId) => (
                                    <span
                                      key={roleId}
                                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(roleId)}`}
                                    >
                                      {getRoleName(roleId)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <div className="flex items-center space-x-4 mt-1">
                                <div className="flex items-center text-sm text-gray-500">
                                  <EnvelopeIcon className="h-4 w-4 mr-1" />
                                  {user.email}
                                </div>
                                <div className="flex items-center text-sm text-gray-500">
                                  <CalendarIcon className="h-4 w-4 mr-1" />
                                  Joined: {formatDate(user.createdAt)}
                                </div>
                                <div className="flex items-center text-sm text-gray-500">
                                  <CalendarIcon className="h-4 w-4 mr-1" />
                                  Last login: {formatDate(user.lastLogin)}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* League Memberships */}
                          {memberships.length > 0 && (
                            <div className="mt-3">
                              <h4 className="text-sm font-medium text-gray-700 mb-2">League Memberships:</h4>
                              <div className="flex flex-wrap gap-2">
                                {memberships.map((membership, index) => (
                                  <div
                                    key={index}
                                    className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800"
                                  >
                                    <UserGroupIcon className="h-3 w-3 mr-1" />
                                    {membership.leagueName}
                                    <span className="ml-1 text-gray-600">
                                      ({membership.role}
                                      {membership.teamNames && membership.teamNames.length > 0 && (
                                        <span> - {membership.teamNames.join(', ')}</span>
                                      )}
                                      )
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
